// ============================================================
// analyticsConfig — shared, fail-soft analytics configuration (Phase 34.0)
// ============================================================
//
// This module is the WEB side of the Phase 34 "analytics contract"
// (see blueprints/phase_34/ANALYTICS_CONTRACT.md). It owns ONLY configuration:
// reading + normalizing env, the enable-gate, the canonical web event-name
// catalog, and a non-PII param sanitizer. It deliberately contains NO gtag.js,
// no network calls, and no DOM access — Phase 34.2's `lib/analytics.js` imports
// from here and does the actual GA4 wiring.
//
// Why a separate config module (vs. inlining in analytics.js)?
//   • The enable-gate + event names are referenced from multiple call-sites
//     (App.jsx route effect, LandingPage/AuthPage/TunnelPanel hooks). One source
//     of truth keeps them from drifting.
//   • It is import-safe everywhere — in CRA builds, in tests, and in any
//     non-browser context — because it never touches `window`.
//
// Fail-soft discipline (same as the Phase 33 helpers): nothing here throws and
// nothing here has side effects on import. If env is missing or malformed,
// analytics is simply OFF.

// ── Env (CRA inlines REACT_APP_* as strings at build time) ─────────────────
// `process` may be undefined in some non-CRA contexts (e.g. a bare test runner);
// guard so importing this file can never throw.
const ENV =
  (typeof process !== 'undefined' && process.env) ? process.env : {};

// GA4 Web Measurement ID, e.g. "G-XXXXXXXXXX". Public by design (gtag exposes it
// in every request), so it is safe to ship in the client bundle.
export const GA4_MEASUREMENT_ID = (ENV.REACT_APP_GA4_MEASUREMENT_ID || '').trim();

// Canonical, fully-qualified site origin used for canonical/OG URLs (Phase 34.1)
// and `page_location` (Phase 34.2). Falls back to the live domain.
export const SITE_URL = (ENV.REACT_APP_SITE_URL || 'https://interceptapi.in')
  .trim()
  .replace(/\/+$/, ''); // never keep a trailing slash

// The master switch. CRA inlines this as the literal string "true"/"false",
// so compare against the string — `Boolean("false")` would be `true`.
const FLAG_ENABLED = String(ENV.REACT_APP_ANALYTICS_ENABLED).toLowerCase() === 'true';

// Analytics is live ONLY when the operator explicitly opted in AND a valid-looking
// Measurement ID is present. A self-hoster who sets neither gets a pure no-op build
// (no GA request is ever made). This is the gate every tracking call must honor.
export function isAnalyticsEnabled() {
  return FLAG_ENABLED && /^G-[A-Z0-9]+$/i.test(GA4_MEASUREMENT_ID);
}

// ── Canonical web event-name catalog ───────────────────────────────────────
// Phase 34.2 fires these; keep the strings here so the hook call-sites and any
// GA4 "custom event" config reference identical names. Mirrors the extension
// catalog in backend/src/lib/analytics-events.js (different surface, one contract).
export const WEB_EVENTS = Object.freeze({
  PAGE_VIEW: 'page_view',
  SIGN_UP_CLICK: 'sign_up_click',
  GOOGLE_SIGNIN_CLICK: 'google_signin_click',
  GOOGLE_SIGNIN_SUCCESS: 'google_signin_success',
  START_TUNNEL_CLICK: 'start_tunnel_click',
  LOGIN_SUBMIT: 'login_submit',
});

// ── Non-PII param sanitizer (shared rule-set with the backend helper) ───────
// Defense-in-depth: even though call-sites are supposed to pass only safe params,
// this strips anything that looks sensitive and enforces GA4's own limits, so a
// careless future hook can never leak an email / token / URL / request body into
// analytics. Pure + total: always returns a plain object, never throws.
const PII_SUBSTRINGS = [
  'email', 'token', 'secret', 'password', 'passwd', 'key', 'auth',
  'cookie', 'session', 'url', 'uri', 'body', 'header', 'payload', 'ip',
];
const MAX_PARAMS = 25;        // GA4: max 25 params per event
const MAX_KEY_LEN = 40;       // GA4: param name ≤ 40 chars
const MAX_VALUE_LEN = 100;    // GA4: text value ≤ 100 chars

export function sanitizeEventParams(params) {
  const out = {};
  if (!params || typeof params !== 'object' || Array.isArray(params)) return out;
  let count = 0;
  for (const [rawKey, rawVal] of Object.entries(params)) {
    if (count >= MAX_PARAMS) break;
    const key = String(rawKey);
    if (!key || key.length > MAX_KEY_LEN) continue;
    const lower = key.toLowerCase();
    if (PII_SUBSTRINGS.some((s) => lower.includes(s))) continue;
    if (typeof rawVal === 'number' && Number.isFinite(rawVal)) {
      out[key] = rawVal;
      count++;
    } else if (typeof rawVal === 'boolean') {
      out[key] = rawVal;
      count++;
    } else if (typeof rawVal === 'string') {
      out[key] = rawVal.slice(0, MAX_VALUE_LEN);
      count++;
    }
    // objects / arrays / null / undefined are dropped (GA4 only accepts scalars)
  }
  return out;
}
